/**
 * Validación Espacial con Turf.js
 * 
 * Valida que coordenadas geocodificadas estén dentro del municipio correcto.
 * Previene errores como "Colomera vs Colomers" a nivel geométrico.
 * 
 * @module lib/spatialValidation
 */

import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import distance from '@turf/distance';
import { point, polygon } from '@turf/helpers';
import type { Polygon, MultiPolygon, Position } from 'geojson';
import { getBoundaryByINE, getBoundaryByName } from '../services/boundaries';
import { getBoundaryLoadStatus } from '../services/boundaries/MunicipalBoundaryService';

// ============================================================================
// TIPOS
// ============================================================================

export interface SpatialValidationResult {
  /** ¿El punto está dentro del polígono? */
  isValid: boolean;
  /** Coordenadas validadas [lon, lat] */
  coordinates: [number, number];
  /** Código INE del municipio */
  codigoINE: string;
  /** Confianza de la validación (0-1) */
  confidence: number;
  /** Mensaje descriptivo */
  message: string;
}

export interface MunicipalBoundary {
  /** Código INE 5 dígitos */
  codigoINE: string;
  /** Nombre del municipio */
  nombre: string;
  /** Geometría GeoJSON del límite municipal */
  geometry: Polygon | MultiPolygon;
}

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Valida si un punto está dentro de un polígono municipal
 * 
 * @param coords - Coordenadas [lon, lat] en WGS84
 * @param boundary - Límite municipal con geometría
 * @returns Resultado de validación
 * 
 * @example
 * const result = isPointInMunicipality([-3.7, 37.2], colomeraBoundary);
 * if (!result.isValid) {
 *   console.warn('Punto fuera del municipio:', result.message);
 * }
 */
export function isPointInMunicipality(
  coords: [number, number],
  boundary: MunicipalBoundary
): SpatialValidationResult {
  try {
    const pt = point(coords);

    let isInside = false;

    if (boundary.geometry.type === 'MultiPolygon') {
      // MultiPolygon: validar contra cada parte
      for (const ring of boundary.geometry.coordinates) {
        const partPoly = polygon(ring as Position[][]);
        if (booleanPointInPolygon(pt, partPoly)) {
          isInside = true;
          break;
        }
      }
    } else {
      // Polygon simple
      const poly = polygon(boundary.geometry.coordinates as Position[][]);
      isInside = booleanPointInPolygon(pt, poly);
    }

    return {
      isValid: isInside,
      coordinates: coords,
      codigoINE: boundary.codigoINE,
      confidence: isInside ? 1.0 : 0.0,
      message: isInside
        ? `Punto válido dentro de ${boundary.nombre}`
        : `Punto FUERA de ${boundary.nombre} (${boundary.codigoINE})`
    };
  } catch (error) {
    return {
      isValid: false,
      coordinates: coords,
      codigoINE: boundary.codigoINE,
      confidence: 0,
      message: `Error validación espacial: ${error instanceof Error ? error.message : 'desconocido'}`
    };
  }
}

/**
 * Valida coordenadas contra el límite municipal esperado
 * Wrapper de alto nivel para uso en GeocodingOrchestrator
 * 
 * @param coords - Coordenadas [lon, lat] WGS84
 * @param expectedMunicipio - Nombre del municipio esperado
 * @param boundary - Límite municipal (o null si no disponible)
 * @returns Resultado de validación
 */
export function validateAgainstBoundary(
  coords: [number, number],
  expectedMunicipio: string,
  boundary: MunicipalBoundary | null
): SpatialValidationResult {
  if (!boundary) {
    return {
      isValid: true, // Asumir válido si no hay límite disponible
      coordinates: coords,
      codigoINE: '',
      confidence: 0.5, // Confianza reducida
      message: `Sin límite municipal disponible para ${expectedMunicipio}`
    };
  }

  return isPointInMunicipality(coords, boundary);
}

// ============================================================================
// DISTANCIA AL CENTRO MUNICIPAL (TRF-02)
// ============================================================================

export interface DistanceResult {
  /** Distancia en kilómetros */
  distanceKm: number;
  /** ¿Distancia razonable? (<threshold) */
  isReasonable: boolean;
  /** Umbral usado (km) */
  thresholdKm: number;
  /** Mensaje descriptivo */
  message: string;
}

export interface DistanceOptions {
  /** Umbral máximo razonable en km (default: 20) */
  maxDistanceKm?: number;
}

/**
 * Calcula distancia entre un punto y el centroide municipal
 * 
 * @param coords - Coordenadas [lon, lat] en WGS84
 * @param centroid - Centroide del municipio [lon, lat]
 * @param options - Opciones de cálculo
 * @returns Distancia en kilómetros y evaluación
 */
export function distanceToMunicipalCenter(
  coords: [number, number],
  centroid: [number, number],
  options: DistanceOptions = {}
): DistanceResult {
  const { maxDistanceKm = 20 } = options;
  
  try {
    const pt1 = point(coords);
    const pt2 = point(centroid);
    
    // Distancia en kilómetros (Turf usa km por defecto)
    const distanceKm = distance(pt1, pt2, { units: 'kilometers' });
    const isReasonable = distanceKm <= maxDistanceKm;
    
    return {
      distanceKm: Math.round(distanceKm * 100) / 100,
      isReasonable,
      thresholdKm: maxDistanceKm,
      message: isReasonable
        ? `Distancia válida: ${distanceKm.toFixed(2)} km del centro municipal`
        : `ALERTA: ${distanceKm.toFixed(2)} km del centro (umbral: ${maxDistanceKm} km)`
    };
  } catch (error) {
    return {
      distanceKm: -1,
      isReasonable: false,
      thresholdKm: maxDistanceKm,
      message: `Error calculando distancia: ${error instanceof Error ? error.message : 'desconocido'}`
    };
  }
}

// ============================================================================
// VALIDACIÓN INTEGRADA CON BOUNDARY SERVICE (TRF-04)
// ============================================================================

/**
 * Valida coordenadas contra límite municipal cargado en cache
 * 
 * @param coords - Coordenadas [lon, lat] en WGS84
 * @param municipio - Nombre o código INE del municipio
 * @returns Resultado de validación espacial
 */
export function validateCoordinatesInMunicipality(
  coords: [number, number],
  municipio: string
): SpatialValidationResult {
  // VALMUNI: se consulta el límite de ESTE municipio (por INE o por nombre), no
  // un indicador global de «límites cargados» que solo reflejaba el primero.
  // Síncrono: solo lee la caché; si el límite no está, la fila queda sin validar.
  const esINE = /^\d{5}$/.test(municipio);
  const boundary = esINE ? getBoundaryByINE(municipio) : getBoundaryByName(municipio);

  if (!boundary) {
    const estado = esINE ? getBoundaryLoadStatus(municipio) : 'no_pedido';
    const motivo = estado === 'sin_limite'
      ? 'R2 no tiene su límite'
      : estado === 'error_red'
        ? 'fallo de red al cargarlo'
        : 'límites municipales no cargados';
    return {
      isValid: true,
      coordinates: coords,
      codigoINE: esINE ? municipio : '',
      confidence: estado === 'no_pedido' ? 0.3 : 0.4,
      message: `Sin validar: límite de "${municipio}" no disponible — ${motivo}`
    };
  }

  return isPointInMunicipality(coords, boundary);
}
